import type { ReactNode } from "react";
import { isEmpty } from "lodash";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { IconButton } from "@/react/components/shared/uikit/HeaderV3/IconButton";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { useCustomColors } from "@circle-react-shared/BuilderUI/ThemeConfiguration/hooks/useCustomColors";
import { Form } from "@circle-react-uikit/Form";
import { FormItemWrapper } from "../../DefaultEditorV2/FormItemWrapper";
import { FieldWrapper } from "./FieldWrapper";

export interface ColorFieldProps {
  childrenWrapper?: ReactNode;
  className?: string;
  hideBorder?: boolean;
  hideDescription?: boolean;
  label: string;
  labelClassName?: string;
  labelWrapperClassName?: string;
  name: string;
  shouldShowAlpha?: boolean;
}

export const ColorField = ({
  name,
  label,
  shouldShowAlpha = true,
  ...rest
}: ColorFieldProps) => {
  const {
    flattenedCssVariables,
    themeColors,
    customColors,
    handleAddCustomColor,
  } = useCustomColors();

  const { getValues, setValue } = useFormContext();
  const value = getValues(name) as string;
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  const onClickRemoveHandler = () => {
    setValue(name, "");
  };

  if (isPropertiesPanelV2) {
    return (
      <Form.Item
        name={name}
        label={label}
        {...rest}
        childrenWrapper={FieldWrapper}
      >
        <span className="flex items-center gap-2">
          <Form.ColorPickerV2
            name={name}
            cssVariables={flattenedCssVariables}
            shouldResolveCssVariable={Boolean(flattenedCssVariables)}
            shouldRenderThemeColors={!isEmpty(themeColors)}
            shouldShowAlpha={shouldShowAlpha}
            themeColors={themeColors}
            customColors={customColors}
            onAddCustomColor={
              !isEmpty(themeColors) ? handleAddCustomColor : undefined
            }
          />
          <span className="h-3 font-mono text-sm leading-none">{value}</span>
        </span>
        <span>
          {value !== "" && (
            <IconButton
              variant="primary"
              className="text-dark"
              name="16-close-v2"
              ariaLabel={t("remove")}
              onClick={onClickRemoveHandler}
            />
          )}
        </span>
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={name}
      label={label}
      hideDescription
      hideBorder
      className="!flex !w-full !items-center !justify-between !p-0"
      labelWrapperClassName="!w-fit"
      childrenWrapper={FormItemWrapper}
      labelClassName="!font-normal text-xs mb-0 mr-2"
    >
      <Form.ColorPickerV2
        name={name}
        cssVariables={flattenedCssVariables}
        shouldResolveCssVariable={Boolean(flattenedCssVariables)}
        shouldRenderThemeColors={!isEmpty(themeColors)}
        shouldShowAlpha={shouldShowAlpha}
        themeColors={themeColors}
        customColors={customColors}
        onAddCustomColor={
          !isEmpty(themeColors) ? handleAddCustomColor : undefined
        }
      />
    </Form.Item>
  );
};
